import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Scroll, Download, Eye, Sparkles, Loader2, AlertCircle, CheckCircle2, Package } from "lucide-react";
import { toast } from "sonner";

/** Pick the most-recently-created doc matching `templateKey`. */
function pickLatest(documents, templateKey) {
  return (documents || [])
    .filter((d) => d.template_key === templateKey)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
}

/**
 * Trust-discipline admin actions panel — renders only for matter_type='trust'.
 * Shows one row per Trust submission document with Generate/Preview/Download
 * controls plus the "Download Trust Submission Bundle" CTA in the header.
 */
const TRUST_TEMPLATES = [
  { key: "trust_deed",                label: "Trust Deed",                                   blurb: "Long-form Inter-Vivos Trust Deed (firm template)." },
  { key: "trust_j401",                label: "J401 — Trust Registration & Amendments",       blurb: "Master submission form with all 9 sections." },
  { key: "trust_beneficiaries_decl",  label: "J450 — Beneficiaries Declaration",             blurb: "Beneficiary table including minors and guardians." },
  { key: "trust_acceptance_trustee",  label: "J417 — Acceptance of Trusteeship",             blurb: "Per-trustee acceptance form (one per trustee)." },
  { key: "trust_sworn_affidavit",     label: "Sworn Affidavit by Independent Trustee",       blurb: "For each independent trustee on the matter." },
  { key: "trust_j405_auditor",        label: "J405 — Auditor / Accountant Undertaking",      blurb: "Auditor's declaration and undertaking to the Master." },
  { key: "trust_instruction_form",    label: "Trust Registration Instruction Form",          blurb: "EH Inc internal instruction sheet." },
  { key: "trust_prescribed_fee",      label: "Annexure B — Master's Prescribed Fee",         blurb: "R 100.00 fee form for proof of payment." },
];

export default function TrustDocumentActions({ matter, documents, onChanged }) {
  if ((matter?.matter_type || matter?.service_type) !== "trust") return null;

  const anyDraft = TRUST_TEMPLATES.some((t) => pickLatest(documents, t.key));

  const downloadBundle = () => {
    api.get(`/admin/matters/${matter.id}/trust-bundle.zip`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/zip" });
        const ref  = (matter.id || "").slice(0, 8).toUpperCase();
        const name = (matter.entities?.trust?.name || matter.client_name || "Trust").replace(/[^A-Za-z0-9]/g, "_").slice(0, 48);
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `EH_Inc_Trust_Submission_${name}_${ref}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
        toast.success("Trust submission bundle downloaded");
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div className="bg-white border border-zinc-200 mb-5" data-testid="trust-doc-actions">
      <div className="px-5 py-3 bg-zinc-900 text-white flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Scroll className="h-4 w-4" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-300">EH Inc · Trust Workspace</div>
            <div className="text-sm font-medium">
              {matter.entities?.trust?.name || "Trust Submission Documents"}
            </div>
          </div>
        </div>
        {anyDraft && (
          <button
            type="button"
            onClick={downloadBundle}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-medium bg-white text-zinc-900 hover:bg-zinc-100"
            data-testid="download-trust-bundle-btn"
          >
            <Package className="h-3.5 w-3.5" />
            Download Trust Submission Bundle (ZIP)
          </button>
        )}
      </div>

      <ul className="divide-y divide-zinc-200">
        {TRUST_TEMPLATES.map((t) => (
          <TrustRow
            key={t.key}
            label={t.label}
            blurb={t.blurb}
            templateKey={t.key}
            matter={matter}
            doc={pickLatest(documents, t.key)}
            onChanged={onChanged}
          />
        ))}
      </ul>
    </div>
  );
}


function TrustRow({ label, blurb, templateKey, matter, doc, onChanged }) {
  const [busy, setBusy]       = useState(false);
  const [missing, setMissing] = useState(null);

  const generate = async () => {
    setBusy(true);
    setMissing(null);
    try {
      const { data } = await api.post(
        `/admin/matters/${matter.id}/documents/generate-structured`,
        { template_key: templateKey },
      );
      toast.success(`${label} generated`, { description: data?.audit_event?.summary || "Draft ready for review." });
      onChanged?.();
    } catch (e) {
      const status = e?.response?.status;
      const body   = e?.response?.data;
      if (status === 422 && body?.blocked) {
        setMissing(body.missing || []);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const previewPdf = () => {
    if (!doc) return;
    api.get(`/admin/documents/${doc.id}/pdf`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url  = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const downloadPdf = () => {
    if (!doc) return;
    api.get(`/admin/documents/${doc.id}/pdf`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${(doc.template_name || label).replace(/\s+/g, "_")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <li className="p-5" data-testid={`trust-row-${templateKey}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="font-medium text-sm text-zinc-900">{label}</div>
          <div className="text-xs text-zinc-500 mt-1">{blurb}</div>
          {doc && (
            <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> Draft generated
              </span>
              <span className="text-zinc-400">·</span>
              <span>{new Date(doc.created_at).toLocaleString()}</span>
              <span className="text-zinc-400">·</span>
              <span className="uppercase tracking-wider text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5">
                {(doc.status || "").replace(/_/g, " ")}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {doc ? (
            <>
              <Button variant="outline" size="sm" className="rounded-none" onClick={previewPdf}
                      data-testid={`preview-${templateKey}-btn`}>
                <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview
              </Button>
              <Button size="sm" className="rounded-none bg-zinc-900 hover:bg-zinc-800" onClick={downloadPdf}
                      data-testid={`download-${templateKey}-btn`}>
                <Download className="h-3.5 w-3.5 mr-1.5" /> Download
              </Button>
              <Link to={`/admin/matters/${matter.id}/documents/${doc.id}`}
                    className="text-xs text-zinc-500 hover:text-zinc-900 px-2"
                    data-testid={`open-${templateKey}-workspace`}>
                Workspace →
              </Link>
            </>
          ) : (
            <Button size="sm" className="rounded-none bg-zinc-900 hover:bg-zinc-800" onClick={generate} disabled={busy}
                    data-testid={`generate-${templateKey}-btn`}>
              {busy
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Generating…</>
                : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate</>}
            </Button>
          )}
        </div>
      </div>

      {missing && missing.length > 0 && (
        <div className="mt-3 border border-amber-300 bg-amber-50 p-3" data-testid={`trust-missing-${templateKey}`}>
          <div className="text-xs font-medium text-amber-900 flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5" />
            Complete required fields to generate {label}
          </div>
          <ul className="text-[12px] text-amber-800 list-disc pl-5 mt-1.5 space-y-0.5">
            {missing.map((f) => <li key={f}>{f}</li>)}
          </ul>
        </div>
      )}
    </li>
  );
}
